// Globals
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { createPartner, getPartnersList } from '../../feature/actionCreators';
import { partnersListSelector } from '../../feature/selectors';

// Components
import { IFormInstance, MessageModal } from '@/components';
import { Skeleton } from '@/ui';
import { CreatePartnerForm } from '../../components';
import { StyledPageWrapper } from './CreatePartnerContainerStyles';

// Helpers
import { TPartnerFormValues } from '../../helpers/types';
import { PartnerDetailsDTO } from '../../dtos';
import { useModal } from '@/hooks';
import { IApiError } from '@/models/apiError.model';

interface ICreatePartnerContainer {
  form: IFormInstance;
  onFormSubmit: () => void;
}

const CreatePartnerContainer = ({ form, onFormSubmit }: ICreatePartnerContainer) => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppState(partnersListSelector);
  const { t } = useTranslation(['partners']);
  const {
    isOpened: isActivationModalOpen,
    openModal: openActivationModal,
    closeModal: closeActivationModal,
  } = useModal();

  const handleActivationModalClose = useCallback(() => {
    closeActivationModal();
    onFormSubmit();
  }, [closeActivationModal, onFormSubmit]);

  const handleSubmit = useCallback(
    async (values: TPartnerFormValues) => {
      try {
        const partnerDetailsDto = new PartnerDetailsDTO(values);
        await dispatch(createPartner(partnerDetailsDto.toCreateDTO())).unwrap();
      } catch (error) {
        form.setFields(
          Object.entries((error as IApiError).errors ?? {}).map(([name, message]) => ({ name, errors: [message] })),
        );
        return;
      }
      await dispatch(getPartnersList({ page: 1 })).unwrap();
      openActivationModal();
    },
    [dispatch, openActivationModal, form],
  );

  return (
    <StyledPageWrapper>
      {isLoading ? <Skeleton /> : <CreatePartnerForm handleSubmit={handleSubmit} isLoading={isLoading} form={form} />}
      <MessageModal
        isOpened={isActivationModalOpen}
        message={t('account_created_title')}
        additionalMessage={t('account_created_activation_text')}
        handleClose={handleActivationModalClose}
        maskClosable={false}
      />
    </StyledPageWrapper>
  );
};

export default CreatePartnerContainer;
